import { useContext, useEffect, useRef, useCallback } from "react";
import { SocketContext } from "./SocketContext";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  addMessage,
  updateAllChatUnreadCount,
  updateAllChatLastMessage,
  updateLastMessageTime,
  updateAllChatReadStatus,
  updateMessageReceived,
  updateMessageRead,
  updateThreadLastActivity,
  updateGroupInfo,
  addNewThread,
  updateConversations,
  groupDeletedAction,
  updateThreadParticipants,
} from "../redux/slices/conversations.slice";
import { updateUserData } from "../redux/slices/user.slice";
import {
  MESSAGE_RECEIVED_EVENT,
  MESSAGE_RECEIVED_STATUS_EVENT,
  MESSAGES_READ_STATUS_EVENT,
  TYPING_EVENT,
  STOP_TYPING_EVENT,
  PROFILE_UPDATED_EVENT,
  GROUP_UPDATED_EVENT,
  GROUP_CREATED_EVENT,
  GROUP_DELETED_EVENT,
  GROUP_LEFT_EVENT,
} from "../utils/socketConstants";
import { playNotifySoundDebounced } from "../utils";
import { updateMessagesReadAPI } from "../redux/api/v3/conversationAPI";

const SocketEventHandlers = () => {
  const socket = useContext(SocketContext);
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state: any) => state.userDetails);
  const { currentThread, currentChatId, threads } = useAppSelector(
    (state: any) => state.conversations
  );

  const userRef = useRef(user);
  const currentThreadRef = useRef(currentThread);
  const currentChatIdRef = useRef(currentChatId);
  const threadsRef = useRef(threads);

  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { currentThreadRef.current = currentThread; }, [currentThread]);
  useEffect(() => { currentChatIdRef.current = currentChatId; }, [currentChatId]);
  useEffect(() => { threadsRef.current = threads; }, [threads]);

  useEffect(() => {
    if (!socket) return;

    // ── MESSAGE_RECEIVED_EVENT ─────────────────────────────────────────────
    const onMessageReceived = (payload: any) => {
      const message = payload.message;
      const u = userRef.current;
      const activeChatId = currentChatIdRef.current;

      const chatId = message.threadId || message.chat;
      const isSentByMe = String(message.sender || message.senderId) === String(u?.id);
      const content = message.content || message.message || "";
      const attachments = message.attachments;

      dispatch(updateAllChatLastMessage({ chatId, value: content }));
      dispatch(updateLastMessageTime({
        threadId: chatId,
        createdAt: message.createdAt,
        lastMessage: content,
      }));
      dispatch(updateThreadLastActivity({
        threadId: chatId,
        lastActivity: content,
        lastActivityType: attachments?.length > 0 ? "attachment" : "message",
        reactorId: null,
      }));
      dispatch(addMessage({ chatId, message }));

      if (isSentByMe) return;
      const isTabVisible = document.visibilityState === "visible";
      const isInThisChat = isTabVisible && String(activeChatId) === String(chatId);

      if (!isInThisChat) {
        dispatch(
          updateAllChatUnreadCount({
            chatId,
            value: 1,
            message,
            isPrivate: message.isGroup === false,
          })
        );
        dispatch(updateAllChatReadStatus({ chatId, value: false }));
        playNotifySoundDebounced();

        dispatch(updateMessageReceived({ chatId, message, userId: u.id }));
        socket.emit(MESSAGE_RECEIVED_STATUS_EVENT, { chatId, message, userId: u.id });
      } else {
        updateMessagesReadAPI({ chatId, userId: u.id, user: u });
        dispatch(updateMessageRead({ chatId, userId: u.id, user: u }));
        dispatch(updateAllChatReadStatus({ chatId, value: true }));
        socket.emit(MESSAGES_READ_STATUS_EVENT, { chatId, userId: u.id });
      }
    };

    // ── MESSAGE_RECEIVED_STATUS_EVENT ──────────────────────────────────────
    const onMessageReceivedStatus = ({ chatId, message, userId }: any) => {
      dispatch(updateMessageReceived({ chatId, message, userId }));
    };

    // ── MESSAGES_READ_STATUS_EVENT ─────────────────────────────────────────
    const onMessagesReadStatus = ({ chatId, userId }: any) => {
      dispatch(updateMessageRead({ chatId, userId }));
      const myId = String(userRef.current?.id);
      if (String(userId) === myId) {
        dispatch(updateAllChatUnreadCount({ chatId, value: 0, reset: true }));
        dispatch(updateAllChatReadStatus({ chatId, value: true }));
      }
    };

    // ── thread_activity_updated ────────────────────────────────────────────
    const onThreadActivityUpdated = (data: any) => {
      dispatch(
        updateThreadLastActivity({
          threadId: data.threadId,
          lastActivity: data.lastActivity,
          lastActivityType: data.lastActivityType,
          reactorId: data.reactorId,
        })
      );
    };

    // ── GROUP_UPDATED_EVENT ────────────────────────────────────────────────
    const onGroupUpdated = ({ groupId, group }: any) => {
      dispatch(
        updateGroupInfo({
          groupId: String(groupId),
          name: group.name,
          profilePicture: group.profile_picture ?? undefined,
        })
      );
    };

    // ── GROUP_CREATED_EVENT ────────────────────────────────────────────────
    const onGroupCreated = ({ thread }: any) => {
      const alreadyHaveIt = (threadsRef.current || []).some(
        (t: any) => String(t.threadId) === String(thread.threadId)
      );
      if (alreadyHaveIt) return;

      dispatch(addNewThread({ thread }));
      dispatch(
        updateAllChatUnreadCount({
          chatId: thread.threadId,
          value: thread.unreadCount ?? 1,
          reset: true,
          isPrivate: false,
        })
      );
    };

    // ── GROUP_DELETED_EVENT ────────────────────────────────────────────────
    const onGroupDeleted = ({ groupId, deletedBy }: any) => {
      dispatch(groupDeletedAction({ groupId: String(groupId) }));

      const isDeleter = String(deletedBy) === String(userRef.current?.id);
      const wasViewingIt =
        String(currentThreadRef.current?.threadId) === String(groupId) ||
        String(currentChatIdRef.current) === String(groupId);

      if (!isDeleter && wasViewingIt) {
        dispatch(updateConversations({ key: "currentThread", value: {} }));
        dispatch(updateConversations({ key: "currentChatId", value: null }));
        window.location.href = "/messages";
      }
    };

    // ── GROUP_LEFT_EVENT ────────────────────────────────────────────────────
    // Only handles OTHER participants leaving — the leaving user's own client
    // already does cleanup + navigation synchronously off the leaveGroup API
    // response (see LeaveGroupModal), so there's no isThisUser branch here.
    // That removes the redundant socket round-trip + hard reload that was
    // racing with the modal's own close/reset and causing it to reappear.
    const onGroupLeft = ({ groupId, userId: whoLeft, updatedParticipants }: any) => {
      const isThisUser = String(whoLeft) === String(userRef.current?.id);
      if (isThisUser) return;

      dispatch(
        updateThreadParticipants({
          groupId,
          updatedParticipants: updatedParticipants ?? [],
        })
      );
    };

    // ── Register ───────────────────────────────────────────────────────────
    socket.on(MESSAGE_RECEIVED_EVENT, onMessageReceived);
    socket.on(MESSAGE_RECEIVED_STATUS_EVENT, onMessageReceivedStatus);
    socket.on(MESSAGES_READ_STATUS_EVENT, onMessagesReadStatus);
    socket.on("thread_activity_updated", onThreadActivityUpdated);
    socket.on(GROUP_UPDATED_EVENT, onGroupUpdated);
    socket.on(GROUP_CREATED_EVENT, onGroupCreated);
    socket.on(GROUP_DELETED_EVENT, onGroupDeleted);
    socket.on(GROUP_LEFT_EVENT, onGroupLeft);

    return () => {
      socket.off(MESSAGE_RECEIVED_EVENT, onMessageReceived);
      socket.off(MESSAGE_RECEIVED_STATUS_EVENT, onMessageReceivedStatus);
      socket.off(MESSAGES_READ_STATUS_EVENT, onMessagesReadStatus);
      socket.off("thread_activity_updated", onThreadActivityUpdated);
      socket.off(GROUP_UPDATED_EVENT, onGroupUpdated);
      socket.off(GROUP_CREATED_EVENT, onGroupCreated);
      socket.off(GROUP_DELETED_EVENT, onGroupDeleted);
      socket.off(GROUP_LEFT_EVENT, onGroupLeft);
    };
  }, [socket, dispatch]);

  return null;
};

export default SocketEventHandlers;